<script>
import { mapActions } from 'vuex'

import MultiSelect from 'vue-multiselect'

import InputWrapper from '@/components/UI/FormGroupWrappers/InputWrapper.vue'

export default {
	name: 'SelectPlots',
	components: {
		MultiSelect,
		InputWrapper,
	},
	props: {
		withTitle: {
			type: Boolean,
			default: true,
		},
		title: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		max: {
			type: Number,
			default: null,
		},
		errors: {
			type: Object,
			default: null,
		},
		name: {
			type: String,
			default: '',
		},
		filters: {
			type: Object,
			default: () => ({}),
		},
		required: {
			type: Boolean,
			default: false,
		},
		multiple: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		value: {
			type: [Object, Array],
			default: null,
		},
		confinesValue: {
			type: Array,
			default: () => [],
		},
		// Если передан id субъекта — список ограничен участками из его квот,
		// иначе используется общий справочник участков.
		userId: {
			type: [Number, String],
			default: null,
		},
		watchValue: null,
		watchValues: {
			type: Array,
			default: () => [],
		},
	},
	emits: ['input'],
	computed: {
		modelValue: {
			get() {
				return this.value
			},
			set(value) {
				this.$emit('input', value)
			},
		},
		options() {
			let plots = this.userId
				? this.$store.state.catalogs.userPlots || []
				: this.$store.state.catalogs.plots || []

			// Always include the currently selected plot if it exists
			let selectedPlot = null
			if (this.value && this.value.id) {
				selectedPlot = plots.find((plot) => plot.id === this.value.id)
				if (!selectedPlot) {
					// If the selected plot is not in the store, create a placeholder
					selectedPlot = this.value
				}
			}

			// Filter by region if provided
			if (this.filters && this.filters.regions && this.filters.regions.length) {
				plots = plots.filter(
					(plot) =>
						(plot.region_id && this.filters.regions.includes(plot.region_id)) ||
						(plot.regions && plot.regions.some((r) => this.filters.regions.includes(r.id))),
				)
			}
			// Filter by district if provided
			if (this.filters && this.filters.districts && this.filters.districts.length) {
				plots = plots.filter(
					(plot) =>
						(plot.district_id && this.filters.districts.includes(plot.district_id)) ||
						(plot.districts && plot.districts.some((d) => this.filters.districts.includes(d.id))),
				)
			}
			// Участки субъекта сужаем по выбранным водоёмам на месте — справочник уже его собственный.
			if (this.userId && this.filters?.reservoirs?.length) {
				// id из query-строки приходят строками — сравниваем нестрого.
				plots = plots.filter((plot) =>
					this.filters.reservoirs.some((id) => id == plot.reservoir_id),
				)
			}
			// Filter by pond (reservoir) if provided
			if (this.filters && this.filters.ponds && this.filters.ponds.length) {
				plots = plots.filter(
					(plot) =>
						(plot.reservoir_id && this.filters.ponds.includes(plot.reservoir_id)) ||
						(plot.ponds && plot.ponds.some((p) => this.filters.ponds.includes(p.id))),
				)
			}
			// Filter by pool if provided
			if (this.filters && this.filters.pools && this.filters.pools.length) {
				plots = plots.filter(
					(plot) =>
						(plot.pool_id && this.filters.pools.includes(plot.pool_id)) ||
						(plot.pools && plot.pools.some((p) => this.filters.pools.includes(p.id))),
				)
			}

			// Add the selected plot to the beginning if it's not already in the filtered list
			if (selectedPlot && !plots.find((plot) => plot.id === selectedPlot.id)) {
				plots.unshift(selectedPlot)
			}

			// Filter by confinesValue (quotas)
			plots = plots.filter((item) => !this.confinesValue?.some((confine) => confine.id === item.id))

			const allItems = [
				{
					source: this.$t('labels.restItems'),
					items: plots,
				},
			]
			if (this.confinesValue?.length) {
				allItems.unshift({
					source: this.$t('labels.byQuota'),
					items: this.confinesValue,
				})
			}
			return allItems
		},
	},
	watch: {
		userId() {
			this._fetchUserPlotList(this.userId)
		},
		watchValues: {
			handler(newValue, oldValue) {
				if (this.userId) return
				if (!this.watchValues?.length || JSON.stringify(newValue) === JSON.stringify(oldValue))
					return

				// Check if we have valid filters to fetch plots
				const hasValidFilters = Object.entries(this.filters).some(
					([, value]) =>
						value &&
						Array.isArray(value) &&
						value.length > 0 &&
						value.every((v) => v !== null && v !== undefined),
				)

				if (hasValidFilters) {
					// Map filter names to API parameter names
					const apiFilters = {}
					if (this.filters.regions) apiFilters.regions = this.filters.regions
					if (this.filters.districts) apiFilters.districts = this.filters.districts
					if (this.filters.ponds) apiFilters.ponds = this.filters.ponds
					if (this.filters.pools) apiFilters.pools = this.filters.pools
					// Бэк отдаёт все участки, только если запрошен мелиоративный вид лова и на дату есть такое разрешение.
					if (this.filters.date) apiFilters.date = this.filters.date
					if (this.filters.fishing_type_code)
						apiFilters.fishing_type_code = this.filters.fishing_type_code

					this._fetchPlots(apiFilters)
				}
			},
		},
	},
	methods: {
		...mapActions(['_fetchPlots', '_fetchUserPlotList']),
	},
	async beforeMount() {
		if (this.userId) {
			await this._fetchUserPlotList(this.userId)
			return
		}
		// Check if we have valid filters to fetch plots with filters
		const hasValidFilters = Object.entries(this.filters).some(
			([, value]) =>
				value &&
				Array.isArray(value) &&
				value.length > 0 &&
				value.every((v) => v !== null && v !== undefined),
		)

		if (hasValidFilters) {
			// Map filter names to API parameter names
			const apiFilters = {}
			if (this.filters.regions) apiFilters.regions = this.filters.regions
			if (this.filters.districts) apiFilters.districts = this.filters.districts
			if (this.filters.ponds) apiFilters.ponds = this.filters.ponds
			if (this.filters.pools) apiFilters.pools = this.filters.pools
			if (this.filters.date) apiFilters.date = this.filters.date
			if (this.filters.fishing_type_code)
				apiFilters.fishing_type_code = this.filters.fishing_type_code

			await this._fetchPlots(apiFilters)
		} else {
			await this._fetchPlots()
		}
	},
}
</script>

<template>
	<InputWrapper
		class="select-region"
		:title="withTitle ? title || '' : ''"
		:is-error="errors && errors.$error"
		:error="errors && !errors.required ? $t('system_message.required_message') : ''"
	>
		<MultiSelect
			track-by="id"
			label="title"
			class="multiselect__check"
			:max="multiple && max"
			:preserve-search="true"
			:required="required"
			:multiple="multiple"
			:close-on-select="!multiple"
			:placeholder="placeholder"
			:disabled="disabled"
			:options="options"
			group-label="source"
			group-values="items"
			:group-select="false"
			v-model="modelValue"
		>
			<template slot="option" slot-scope="{ option }">
				<span v-if="option.$isLabel && confinesValue?.length" class="group-title">{{
					option.$groupLabel || ''
				}}</span>
				<span v-else>{{
					(option?.reservoir?.title ? `${option?.reservoir?.title} - ` : '') + (option?.title || '')
				}}</span>
			</template>
			<template slot="noResult">{{ $t('components.not_found') }}</template>
			<template slot="noOptions">{{ $t('components.not_found') }}</template>
			<template slot="placeholder">{{ placeholder || $t('placeholder.choose') }}</template>
		</MultiSelect>
	</InputWrapper>
</template>

<style>
.select-region .multiselect__tags {
	padding-left: 20px;
	border-radius: 5px;
	background: #ffffff;
	border: 1px solid #157fee !important;
	font-weight: 400;
	font-size: 14px;
	color: #0a091d;
	line-height: 1.2;
	width: 100%;
}

.select-region .multiselect--disabled .multiselect__tags {
	border: 1px solid #d3e7fb;
}

.select-region .multiselect--disabled .multiselect__placeholder {
	margin-bottom: 0;
}

.select-region .multiselect__single {
	margin-bottom: 0;
}

.select-region .input-body--error.input-body .multiselect__tags {
	border-color: #f3574d;
}

.select-region .input-body--error + .input-required {
	display: block;
}
</style>
